import React, { useState, useEffect } from 'react';
import {
  Calendar,
  DollarSign,
  Users,
  Building2,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  FileText,
  RotateCcw,
} from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import SearchableUserDropdown from './SearchableUserDropdown';
import StyledDropdown from './StyledDropdown';
import PdfPreviewModal from './PdfPreviewModal';
import { generateCommissionComparisonPDF, generateCommissionPDF, generateCommissionSummaryPDF } from '../utils/pdfGenerator';

const CommissionManagement = () => {
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [retroactiveCommissions, setRetroactiveCommissions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({
    totalPersonal: 0,
    totalTLOverride: 0,
    totalGLOverride: 0,
    totalRetroactive: 0,
    totalEligible: 0,
  });

  // Filters
  const currentYear = new Date().getFullYear();
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterGroup, setFilterGroup] = useState('');
  const [compareGroup1, setCompareGroup1] = useState('');
  const [compareGroup2, setCompareGroup2] = useState('');
  const [filterUser, setFilterUser] = useState('');

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState({});
  
  // Export state
  const [exporting, setExporting] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfDownloadName, setPdfDownloadName] = useState('document.pdf');

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setPdfPreviewOpen(false);
  };

  const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => currentYear + 1 - i);
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Fetch groups and users
  const fetchFiltersData = async () => {
    try {
      const [groupsRes, usersRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/groups`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/users`, { headers: getAuthHeaders() }),
      ]);

      const groupsData = await groupsRes.json();
      const usersData = await usersRes.json();

      if (groupsData.success) {
        setGroups(groupsData.data);
      }
      if (usersData.success) {
        // Filter out Admin and Superadmin - they don't have commissions
        const salesUsers = usersData.data.filter(
          u => u.role !== 'Admin' && u.role !== 'Superadmin'
        );
        setUsers(salesUsers);
      }
    } catch (err) {
      console.error('Error fetching filter data:', err);
    }
  };

  // Fetch commissions data
  const fetchCommissions = async () => {
    try {
      setLoading(true);

      let url = `${getApiUrl()}/api/commissions/all?month=${filterMonth}&year=${filterYear}`;
      if (filterGroup) url += `&groupId=${filterGroup}`;
      if (filterUser) url += `&userId=${filterUser}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      const data = await res.json();

      if (data.success) {
        // Separate current month commissions from retroactive
        const currentMonthData = data.data.filter(c => !c.isRetroactive);
        const retroData = data.data.filter(c => c.isRetroactive);
        
        // Group current month commissions by user
        const groupedByUser = {};
        
        currentMonthData.forEach(comm => {
          const userId = comm.userId;
          if (!groupedByUser[userId]) {
            groupedByUser[userId] = {
              user: comm.user,
              commissions: [],
              retroactiveCommissions: [],
              totalPersonal: 0,
              totalTLOverride: 0,
              totalGLOverride: 0,
              totalRetroactive: 0,
              totalEligible: 0,
            };
          }
          
          groupedByUser[userId].commissions.push(comm);
          
          // Use eligibleAmount for actual payable commission
          const eligibleAmount = parseFloat(comm.eligibleAmount || 0);
          
          if (comm.commissionType === 'Personal') {
            groupedByUser[userId].totalPersonal += eligibleAmount;
            groupedByUser[userId].totalEligible += eligibleAmount;
          } else if (comm.commissionType === 'TL Override') {
            groupedByUser[userId].totalTLOverride += eligibleAmount;
            groupedByUser[userId].totalEligible += eligibleAmount;
          } else if (comm.commissionType === 'GL Override') {
            groupedByUser[userId].totalGLOverride += eligibleAmount;
            groupedByUser[userId].totalEligible += eligibleAmount;
          }
        });

        // Add retroactive commissions to their respective users
        retroData.forEach(comm => {
          const userId = comm.userId;
          if (!groupedByUser[userId]) {
            groupedByUser[userId] = {
              user: comm.user,
              commissions: [],
              retroactiveCommissions: [],
              totalPersonal: 0,
              totalTLOverride: 0,
              totalGLOverride: 0,
              totalRetroactive: 0,
              totalEligible: 0,
            };
          }
          
          groupedByUser[userId].retroactiveCommissions.push(comm);
          
          // Retroactive is always the commission amount (threshold was met)
          const retroAmount = parseFloat(comm.commissionAmount || 0);
          groupedByUser[userId].totalRetroactive += retroAmount;
          groupedByUser[userId].totalEligible += retroAmount;
        });

        // Convert to array and sort by total eligible descending
        const commissionsArray = Object.values(groupedByUser);
        commissionsArray.sort((a, b) => b.totalEligible - a.totalEligible);

        setCommissions(commissionsArray);
        setRetroactiveCommissions(retroData);

        // Calculate summary
        const summaryData = commissionsArray.reduce((acc, c) => ({
          totalPersonal: acc.totalPersonal + c.totalPersonal,
          totalTLOverride: acc.totalTLOverride + c.totalTLOverride,
          totalGLOverride: acc.totalGLOverride + c.totalGLOverride,
          totalRetroactive: acc.totalRetroactive + c.totalRetroactive,
          totalEligible: acc.totalEligible + c.totalEligible,
        }), { totalPersonal: 0, totalTLOverride: 0, totalGLOverride: 0, totalRetroactive: 0, totalEligible: 0 });

        setSummary(summaryData);
      }
    } catch (err) {
      console.error('Error fetching commissions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Reset user filter when group changes
  useEffect(() => {
    if (filterGroup) {
      setFilterUser(''); // Reset user filter when group changes
    }
  }, [filterGroup]);

  useEffect(() => {
    fetchFiltersData();
  }, []);

  useEffect(() => {
    fetchCommissions();
  }, [filterMonth, filterYear, filterGroup, filterUser]);

  const toggleExpand = (userId) => {
    setExpandedRows(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Check if export needs confirmation
  const handleExportClick = () => {
    if (filterUser) {
      // Specific user selected - export directly
      handleExportPDF();
    } else {
      // No specific user - show confirmation
      setShowExportConfirm(true);
    }
  };

  // Export PDF for filtered user(s)
  const handleExportPDF = async () => {
    setShowExportConfirm(false);
    setExporting(true);
    
    try {
      const monthName = months.find(m => m.value === filterMonth)?.label || '';
      const period = `${monthName} ${filterYear}`;
      
      // If single user selected - generate individual commission statement
      if (filterUser) {
        try {
          const response = await fetch(
            `${getApiUrl()}/api/commissions/my-commissions?month=${filterMonth}&year=${filterYear}&userId=${filterUser}`,
            { headers: getAuthHeaders() }
          );
          
          const result = await response.json();
          
          if (result.success && result.data?.summary?.totalEligible > 0) {
            const userInfo = {
              name: result.data?.user?.name || 'N/A',
              accountId: result.data?.user?.accountId || 'N/A',
              code: result.data?.user?.accountId || 'N/A',
              role: result.data?.user?.role || 'N/A',
              group: result.data?.user?.group || 'N/A',
              reportsTo: result.data?.user?.reportsTo || 'N/A',
            };

            const slug = `${filterMonth}-${filterYear}`;
            const url = await generateCommissionPDF(result.data, userInfo, period);
            setPdfDownloadName(`commission-statement-${slug}.pdf`);
            setPdfPreviewUrl(url);
            setPdfPreviewOpen(true);
          } else {
            alert('No commission records found for the selected period.');
          }
        } catch (err) {
          console.error('Export error:', err);
          alert('Failed to generate PDF. Please try again.');
        }
      } else {
        // Multiple users - generate summary report (all users or by group)
        const summaryData = [];

        const hasCompare = !!compareGroup1 && !!compareGroup2;
        const compareIdA = hasCompare ? parseInt(compareGroup1, 10) : null;
        const compareIdB = hasCompare ? parseInt(compareGroup2, 10) : null;
        
        // Get the group name if filtering by group
        const selectedGroup = filterGroup
          ? groups.find(g => g.id === parseInt(filterGroup))?.name
          : null;
        
        // Get relevant users - all users filtered by group if filterGroup is set
        const relevantUsers = hasCompare
          ? users.filter((u) => [compareIdA, compareIdB].includes(u.groupId))
          : (filterGroup
            ? users.filter(u => u.groupId === parseInt(filterGroup))
            : users);
        
        if (relevantUsers.length === 0) {
          alert('No users found for the selected criteria.');
          setExporting(false);
          return;
        }
        
        // Create a map of commissions by user ID for quick lookup
        const commissionMap = {};
        commissions.forEach(commission => {
          commissionMap[commission.user?.id] = {
            personal: commission.totalPersonal || 0,
            teamOverride: commission.totalTLOverride || 0,
            groupOverride: commission.totalGLOverride || 0,
            retroactive: commission.totalRetroactive || 0,
            total: commission.totalEligible || 0,
          };
        });
        
        // Build summary data for ALL users (including those with zero commission)
        relevantUsers.forEach(user => {
          const userCommission = commissionMap[user.id] || {
            personal: 0,
            teamOverride: 0,
            groupOverride: 0,
            retroactive: 0,
            total: 0,
          };
          
          summaryData.push({
            name: user.name || 'N/A',
            code: user.accountId || 'N/A',
            role: user.role || 'N/A',
            group: user.group?.name || 'Unassigned',
            personal: userCommission.personal,
            teamOverride: userCommission.teamOverride,
            groupOverride: userCommission.groupOverride,
            retroactive: userCommission.retroactive,
            total: userCommission.total,
          });
        });
        
        const slug = `${filterMonth}-${filterYear}`;

        if (hasCompare) {
          if (compareIdA === compareIdB) {
            alert('Please select two different groups to compare.');
            setExporting(false);
            return;
          }

          const groupNameA = groups.find((g) => g.id === compareIdA)?.name || 'Unassigned';
          const groupNameB = groups.find((g) => g.id === compareIdB)?.name || 'Unassigned';

          const url = await generateCommissionComparisonPDF(summaryData, period, groupNameA, groupNameB);
          setPdfDownloadName(`commission-comparison-${slug}.pdf`);
          setPdfPreviewUrl(url);
          setPdfPreviewOpen(true);
        } else {
          // Include retroactive summary info
          const hasRetroactive = summary.totalRetroactive > 0;
          const url = await generateCommissionSummaryPDF(summaryData, period, selectedGroup, hasRetroactive ? summary.totalRetroactive : 0);
          setPdfDownloadName(`commission-summary-${slug}.pdf`);
          setPdfPreviewUrl(url);
          setPdfPreviewOpen(true);
        }
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Pagination
  const {
    currentPage,
    paginatedItems: paginatedCommissions,
    totalItems,
    handlePageChange,
  } = usePagination(commissions, 10);

  if (loading && commissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">
            Commission Management
          </h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">
            View and manage all user commissions
          </p>
        </div>
        <button
          onClick={handleExportClick}
          disabled={exporting}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Export PDF"
        >
          <FileText size={20} />
          {exporting ? 'Generating...' : 'Export PDF'}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Month Filter */}
          <StyledDropdown
            options={months.map(m => ({ value: m.value, label: m.label }))}
            value={filterMonth}
            onChange={(val) => setFilterMonth(parseInt(val))}
            placeholder="Select Month"
            icon={Calendar}
            className="min-w-[150px]"
          />

          {/* Year Filter */}
          <StyledDropdown
            options={years.map(y => ({ value: y, label: y.toString() }))}
            value={filterYear}
            onChange={(val) => setFilterYear(parseInt(val))}
            placeholder="Select Year"
            className="min-w-[120px]"
          />

          {/* Group Filter */}
          <StyledDropdown
            options={[
              { value: '', label: 'All Groups' },
              ...groups.map(g => ({ value: g.id.toString(), label: g.name }))
            ]}
            value={filterGroup}
            onChange={(val) => {
              setCompareGroup1('');
              setCompareGroup2('');
              setFilterGroup(val);
            }}
            placeholder="All Groups"
            icon={Building2}
            showClear={true}
            className="min-w-[150px]"
            disabled={!!compareGroup1 || !!compareGroup2}
          />

          {/* Compare Group 1 */}
          <StyledDropdown
            options={groups.map(g => ({ value: g.id.toString(), label: g.name }))}
            value={compareGroup1}
            onChange={(val) => {
              setCompareGroup1(val);
              setCompareGroup2('');
              setFilterGroup('');
              setFilterUser('');
            }}
            placeholder="Compare Group 1"
            icon={Building2}
            showClear={true}
            className="min-w-[170px]"
          />

          {/* Compare Group 2 */}
          <StyledDropdown
            options={groups.map(g => ({ value: g.id.toString(), label: g.name }))}
            value={compareGroup2}
            onChange={(val) => {
              setCompareGroup2(val);
              setFilterGroup('');
              setFilterUser('');
            }}
            placeholder="Compare Group 2"
            icon={Building2}
            showClear={true}
            className="min-w-[170px]"
            disabled={!compareGroup1}
          />

          {/* User Filter */}
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-400 dark:text-dark-muted" />
            <SearchableUserDropdown
              users={filterGroup ? users.filter(u => u.group?.id === parseInt(filterGroup)) : users}
              value={filterUser}
              onChange={setFilterUser}
              placeholder="All Users"
              maxResults={50}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${summary.totalRetroactive > 0 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <DollarSign className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-dark-muted">Total Personal</p>
              <p className="text-xl font-bold text-gray-900 dark:text-dark-text">
                {formatCurrency(summary.totalPersonal)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-dark-muted">Total TL Override</p>
              <p className="text-xl font-bold text-gray-900 dark:text-dark-text">
                {formatCurrency(summary.totalTLOverride)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-dark-muted">Total GL Override</p>
              <p className="text-xl font-bold text-gray-900 dark:text-dark-text">
                {formatCurrency(summary.totalGLOverride)}
              </p>
            </div>
          </div>
        </div>

        {/* Retroactive Card - only show if there are retroactive commissions */}
        {summary.totalRetroactive > 0 && (
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-orange-200 dark:border-orange-900/50 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <RotateCcw className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">Retroactive</p>
                <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                  {formatCurrency(summary.totalRetroactive)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl shadow-sm p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-primary-100">Total Eligible</p>
              <p className="text-xl font-bold">{formatCurrency(summary.totalEligible)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Commissions Table */}
      <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Personal
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  TL Override
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  GL Override
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {paginatedCommissions.length > 0 ? (
                paginatedCommissions.map((item) => (
                  <React.Fragment key={item.user?.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {item.user?.name?.charAt(0) || '?'}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-dark-text">
                            {item.user?.name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-dark-muted">
                        {item.user?.role?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-dark-muted">
                        {item.user?.group?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900 dark:text-dark-text">
                        {formatCurrency(item.totalPersonal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900 dark:text-dark-text">
                        {formatCurrency(item.totalTLOverride)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900 dark:text-dark-text">
                        {formatCurrency(item.totalGLOverride)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-primary-600 dark:text-primary-400">
                        {formatCurrency(item.totalEligible)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => toggleExpand(item.user?.id)}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          title="View Details"
                        >
                          {expandedRows[item.user?.id] ? (
                            <ChevronUp className="w-5 h-5 text-gray-500 dark:text-dark-muted" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-dark-muted" />
                          )}
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded Details */}
                    {expandedRows[item.user?.id] && (
                      <tr>
                        <td colSpan={8} className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50">
                          <div className="space-y-4">
                            {/* Current Month Commission Details */}
                            {item.commissions.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
                                  Commission Details (Current Month)
                                </p>
                                <div className="space-y-1 text-sm">
                                  {item.commissions.map((comm, idx) => {
                                    const potentialAmount = parseFloat(comm.commissionAmount || 0);
                                    const eligibleAmount = parseFloat(comm.eligibleAmount || 0);
                                    const isEligible = eligibleAmount > 0;
                                    
                                    return (
                                      <div 
                                        key={idx}
                                        className={`flex items-center justify-between py-1 px-3 rounded-lg ${
                                          isEligible 
                                            ? 'bg-white dark:bg-dark-card' 
                                            : 'bg-orange-50 dark:bg-orange-900/10'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            comm.commissionType === 'Personal' 
                                              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                                              : comm.commissionType === 'TL Override'
                                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                          }`}>
                                            {comm.commissionType}
                                          </span>
                                          <span className="text-gray-600 dark:text-dark-muted">
                                            {comm.sale?.product?.name || 'Product'}
                                          </span>
                                          <span className="text-gray-400 dark:text-dark-muted">
                                            ({(parseFloat(comm.commissionRate) * 100).toFixed(2)}%)
                                          </span>
                                          {!isEligible && comm.commissionType !== 'Personal' && (
                                            <span className="text-xs text-orange-600 dark:text-orange-400">
                                              (Threshold not met)
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-right">
                                          <span className={`font-medium ${
                                            isEligible 
                                              ? 'text-gray-900 dark:text-dark-text' 
                                              : 'text-orange-600 dark:text-orange-400'
                                          }`}>
                                            {formatCurrency(eligibleAmount)}
                                          </span>
                                          {!isEligible && potentialAmount > 0 && (
                                            <span className="text-xs text-gray-400 dark:text-dark-muted ml-2">
                                              (Potential: {formatCurrency(potentialAmount)})
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Retroactive Commissions */}
                            {item.retroactiveCommissions?.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-orange-600 dark:text-orange-400 mb-2 flex items-center gap-2">
                                  <RotateCcw className="w-4 h-4" />
                                  Retroactive Commission (From Prior Period Sales)
                                </p>
                                <p className="text-xs text-gray-500 dark:text-dark-muted mb-2">
                                  These commissions from previous months became eligible this month when threshold was met.
                                </p>
                                <div className="space-y-1 text-sm">
                                  {item.retroactiveCommissions.map((comm, idx) => {
                                    const amount = parseFloat(comm.commissionAmount || 0);
                                    const saleDate = new Date(comm.sale?.date).toLocaleDateString('en-MY', {
                                      day: 'numeric', month: 'short', year: 'numeric'
                                    });
                                    
                                    return (
                                      <div 
                                        key={`retro-${idx}`}
                                        className="flex items-center justify-between py-1 px-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900/30"
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            comm.commissionType === 'TL Override'
                                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                          }`}>
                                            {comm.commissionType}
                                          </span>
                                          <span className="text-gray-600 dark:text-dark-muted">
                                            {comm.sale?.product?.name || 'Product'}
                                          </span>
                                          <span className="text-gray-400 dark:text-dark-muted">
                                            ({(parseFloat(comm.commissionRate) * 100).toFixed(2)}%)
                                          </span>
                                          <span className="text-xs text-orange-600 dark:text-orange-400">
                                            Sale: {saleDate}
                                          </span>
                                        </div>
                                        <div className="text-right">
                                          <span className="font-medium text-orange-600 dark:text-orange-400">
                                            {formatCurrency(amount)}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-slate-600" />
                    <p className="text-gray-500 dark:text-dark-muted font-medium">
                      No commissions found
                    </p>
                    <p className="text-sm text-gray-400 dark:text-dark-muted mt-1">
                      Try adjusting your filters or select a different period
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {commissions.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={10}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Export Confirmation Modal */}
      {showExportConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
              Generate Summary Report?
            </h3>
            <p className="text-gray-600 dark:text-dark-muted mb-4">
              {compareGroup1 && compareGroup2 ? (
                <>
                  You are about to generate a <strong>Commission Comparison Report</strong> for{' '}
                  <strong>{groups.find(g => g.id === parseInt(compareGroup1))?.name || 'Group 1'}</strong> and{' '}
                  <strong>{groups.find(g => g.id === parseInt(compareGroup2))?.name || 'Group 2'}</strong>. The report will be side-by-side in landscape.
                  Since you are not selecting a specific user, this export will use the chosen month/year.
                </>
              ) : !filterUser && !filterGroup ? (
                <>
                  You are about to generate a <strong>Commission Summary Report</strong> for <strong>all users</strong> with
                  commissions in the selected period. The report will be grouped by group.
                  Since you are not selecting a specific user, the export will generate the summary for the chosen month/year.
                </>
              ) : filterGroup && !filterUser ? (
                <>
                  You are about to generate a <strong>Commission Summary Report</strong> for{' '}
                  <strong>{groups.find(g => g.id === parseInt(filterGroup))?.name || 'the selected group'}</strong>.
                  Since you are not selecting a specific user, the export will generate the summary for the chosen month/year.
                </>
              ) : null}
            </p>
            <p className="text-sm text-primary-600 dark:text-primary-400 mb-6">
              A single PDF opens in a preview window. Use Save / Download to save the file (on phones this opens the share sheet, including Save to Files).
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExportConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-dark-text bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors"
              >
                {compareGroup1 && compareGroup2 ? 'Generate Comparison' : 'Generate Summary'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        pdfUrl={pdfPreviewUrl}
        title="Commission report preview"
        downloadFilename={pdfDownloadName}
        onClose={closePdfPreview}
      />
    </div>
  );
};

export default CommissionManagement;


